/**
 * DocumentsView — Mes Documents (driver documents with upload)
 * Shows required documents with review status and upload capability.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { useProfileCache } from '../../../hooks/useProfileCache';
import axios from 'axios';
import {
  CheckCircle, AlertCircle, Clock, XCircle, Upload, Loader2, FileText
} from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function DocumentsView({ user }) {
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const docsCache = useProfileCache(`docs_${user?.id}`);

  const fetchDocs = useCallback(async (force = false) => {
    const cached = !force && docsCache.get();
    if (cached) { setRequiredDocs(cached); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/driver/required-documents`, { headers: getHeaders() });
      if (res.data?.required) { setRequiredDocs(res.data.required); docsCache.set(res.data.required); }
    } catch { /* silent */ }
    setLoading(false);
  }, [docsCache]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;

  return <DocumentsList docs={requiredDocs} onUpload={async (docType, file) => {
    const formData = new FormData();
    formData.append('document_type', docType);
    formData.append('file', file);
    try {
      await axios.post(`${API}/driver/documents/upload`, formData, { headers: getHeaders() });
      docsCache.invalidate();
      fetchDocs(true);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur upload'); }
  }} />;
}

function DocumentsList({ docs, onUpload }) {
  const [uploading, setUploading] = useState(null);

  const handleFileChange = async (docType, file) => {
    setUploading(docType);
    try { await onUpload(docType, file); } finally { setUploading(null); }
  };

  const isIndependent = docs.some(d => d.type === 'kbis' || d.type === 'rc_pro');
  const businessDocs = docs.filter(d => d.type === 'kbis' || d.type === 'rc_pro');
  const personalDocs = docs.filter(d => d.type !== 'kbis' && d.type !== 'rc_pro');

  const renderDoc = (doc) => {
    const isApproved = doc.status === 'APPROVED' || doc.review_status === 'approved';
    const isRejected = doc.status === 'REJECTED' || doc.review_status === 'rejected';
    const needsUpload = !doc.uploaded || isRejected;
    const isUploading = uploading === doc.type;

    return (
      <div key={doc.type}
        className={`p-3 rounded-lg border ${
          isApproved ? 'bg-emerald-500/5 border-emerald-500/15'
          : isRejected ? 'bg-red-500/5 border-red-500/15'
          : doc.uploaded ? 'bg-amber-500/5 border-amber-500/15'
          : 'bg-white/[0.02] border-white/6'
        }`}
        data-testid={`doc-status-${doc.type}`}>
        <div className="flex items-center gap-2.5">
          {isApproved ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
          : isRejected ? <XCircle className="w-4 h-4 text-red-400 shrink-0" />
          : doc.uploaded ? <Clock className="w-4 h-4 text-amber-400 shrink-0" />
          : <AlertCircle className="w-4 h-4 text-white/30 shrink-0" />}
          <span className={`text-xs font-medium flex-1 ${
            isApproved ? 'text-emerald-400' : isRejected ? 'text-red-400' : doc.uploaded ? 'text-amber-400' : 'text-white/60'
          }`}>{doc.label}</span>
          <span className={`text-[10px] ${
            isApproved ? 'text-emerald-400/70' : isRejected ? 'text-red-400/70' : doc.uploaded ? 'text-amber-400/70' : 'text-white/30'
          }`}>
            {isApproved ? 'Approuve' : isRejected ? 'Rejete' : doc.uploaded ? 'En verification' : 'Requis'}
          </span>
        </div>
        {isRejected && doc.rejection_reason && (
          <p className="text-red-400/60 text-[10px] mt-1.5 ml-6">Raison : {doc.rejection_reason}</p>
        )}
        {needsUpload && (
          <label className={`mt-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg min-h-[44px] touch-manipulation transition-colors text-xs font-medium
            ${isUploading ? 'bg-[#333] text-[#666] cursor-wait' : 'bg-[#C9A24D]/10 text-[#C9A24D] hover:bg-[#C9A24D]/20 cursor-pointer'}`}
            data-testid={`upload-btn-${doc.type}`}>
            {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {isUploading ? 'Envoi...' : isRejected ? 'Renvoyer le document' : 'Choisir un fichier'}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" disabled={isUploading}
              onChange={(e) => { if (e.target.files?.[0]) handleFileChange(doc.type, e.target.files[0]); }} />
          </label>
        )}
      </div>
    );
  };

  if (docs.length === 0) return <p className="text-sm text-white/40 text-center py-8">Aucun document requis</p>;

  return (
    <div className="space-y-2" data-testid="documents-list">
      {isIndependent && businessDocs.length > 0 && (
        <>
          <p className="text-white/50 text-[10px] font-semibold uppercase tracking-wider mt-1">Documents entreprise (Independant)</p>
          {businessDocs.map(renderDoc)}
          <div className="border-t border-white/6 my-2" />
          <p className="text-white/50 text-[10px] font-semibold uppercase tracking-wider">Documents personnels</p>
        </>
      )}
      {personalDocs.map(renderDoc)}
    </div>
  );
}
